import React, { useState, useEffect } from 'react';
import { Plus, Minus } from 'lucide-react';

const faqsData = [
  {
    id: 1,
    question: "What is DigitMoney and how does it work?",
    answer:
      "DigitMoney is a digital lending marketplace that helps you find the right loan for your needs. We connect you with trusted  lending partners who offer personal, business, and debt consolidation loans. With a single application, you get personalized loan offers from lenders that best match your profile.",
  },
  {
    id: 2,
    question: "How does DigitMoney help me get the best loan?",
    answer:
      "DigitMoney simplifies your loan search by matching your profile with top lending institutions based on your needs, income, and credit history. Instead of applying to multiple places, you get personalized loan offers in one place—making it faster, easier, and more likely that you’ll get the best possible deal with minimal effort.",
  },
  {
    id: 3,
    question: "What types of loans can I find on DigitMoney?",
    answer:
      "We currently help you access three types of loans tailored to your needs:\n\n" +
      "• Personal Loans – Ideal when you need quick funds during unexpected expenses, major life events like weddings, travel, or education-related costs.\n" +
      "• Business Loans – Designed to support day-to-day operations, expand your business, or invest in growth opportunities.\n" +
      "• Debt Consolidation Loans – A smart way to simplify your finances by combining existing loans or credit card balances into a single, low interest, more manageable EMI.",
  },
  {
    id: 4,
    question: "Is my data safe on DigitMoney?",
    answer:
      "Yes, absolutely. We take your privacy seriously and use encryption and secure systems to protect your personal and financial information.",
  },
  {
    id: 5,
    question: "Will I definitely get a loan if I apply?",
    answer:
      "Loan approvals are based on the lending partner's eligibility criteria like credit score, income, and documents. DigitMoney improves your chances by showing you lenders who are most likely to approve your application.",
  },
  {
    id: 6,
    question: "How long does it take to get a loan through DigitMoney?",
    answer:
      "It usually takes 24–48 hours for approval after you submit all required documents. The final disbursal timeline depends on the lending partner’s internal process.",
  },
  {
    id: 7,
    question: "What documents do I need to apply?",
    answer:
      "For Salaried Individuals:\n" +
      "1. PAN Card\n" +
      "2. Aadhaar Card\n" +
      "3. Electricity Bill\n" +
      "4. Last 3 Months Bank Statement\n" +
      "5. Last 3 Months Salary Slips\n\n" +
      "For Self-Employed Individuals:\n" +
      "1. PAN Card (Individual)\n" +
      "2. PAN Card (Company)\n" +
      "3. Aadhaar Card\n" +
      "4. GST Certificate\n" +
      "5. Electricity Bill\n" +
      "6. Last 2 Years ITR\n" +
      "7. Last 1 Year Bank Statement",
  },
  {
    id: 8,
    question: "Is there a fee to use DigitMoney?",
    answer:
      "No, DigitMoney is completely free to use. However, lending partners may charge a processing fee or GST, which will be clearly shown to you before you choose to go ahead with a loan.",
  },
  {
    id: 9,
    question: "What is a debt consolidation loan and how does it help?",
    answer:
      "A debt consolidation loan helps you combine multiple loans or credit card dues into a single loan with one EMI. This can reduce your overall interest burden and make repayment easier to manage.",
  },
  {
    id: 10,
    question: "What is the eligibility to apply for a loan through DigitMoney?",
    answer:
      "Eligibility varies by lender, but generally, you should be:\n" +
      "• An Indian citizen aged between 21–60 years\n" +
      "• Either salaried or self-employed with a steady income\n" +
      "• Holding valid KYC documents and a reasonable credit score (usually 680+ for most partners)",
  },
  {
    id: 11,
    question: "How will I know the status of my application?",
    answer:
      "You’ll get regular updates via WhatsApp, SMS or email. You can also log in to your DigitMoney anytime to check the current status and any pending requirements.",
  },
  {
    id: 12,
    question: "Can I apply again if my loan application is rejected?",
    answer:
      "Yes, you can re-apply. We recommend waiting a few weeks and reviewing the reason for rejection—like low credit score or missing documents. DigitMoney may also suggest alternative lenders better suited to your profile.",
  },
];

const Faqs = () => {
  const [activeIndex, setActiveIndex] = useState(1);
  const [showAllMobile, setShowAllMobile] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleIndex = (id) => {
    setActiveIndex((prev) => (prev === id ? null : id));
  };

  const halfLength = Math.ceil(faqsData.length / 2);
  const leftFAQs = faqsData.slice(0, halfLength);
  const rightFAQs = faqsData.slice(halfLength);
  const visibleMobileFaqs = showAllMobile ? faqsData : faqsData.slice(0, 4);

  const renderFaqBlock = (data) => (
    <div className="flex flex-col gap-3 w-full">
      {data.map((faq) => {
        const isOpen = activeIndex === faq.id;
        return (
          <div
            style={{ fontFamily: "Atkinson-regular" }}
            key={faq.id}
            className={`rounded-lg p-4 border-[1px] transition-colors duration-300 ${isOpen
              ? 'bg-white text-black border-white font-semibold'
              : 'bg-black text-[#717171] border-[#363636]'
              }`}
          >
            <div
              className="flex justify-between items-start cursor-pointer gap-3"
              onClick={() => toggleIndex(faq.id)}
            >
              <h3 className="font-semibold text-sm md:text-base leading-tight">{faq.question}</h3>
              <div className="flex-shrink-0 w-5 h-5 bg-green-400 rounded-full flex items-center justify-center">
                {isOpen ? (
                  <Minus className="w-3 h-3 text-white" />
                ) : (
                  <Plus className="w-3 h-3 text-black" />
                )}
              </div>
            </div>

            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[1000px] mt-4 opacity-100' : 'max-h-0 opacity-0'
                }`}
            >
              {faq.answer.split('\n').map((line, index) => (
                <p key={index} className="text-sm text-gray-600 leading-relaxed">
                  {line}
                </p>
              ))}

            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <section id='faq' className="bg-black text-white min-h-screen px-4 py-5 md:px-8  md:py-10">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 style={{ fontFamily: 'myFirstFont' }} className="text-4xl md:text-5xl font-bold text-green-400 mb-2">FAQs</h1>
          <p style={{ fontFamily: 'myFirstFont' }} className="text-gray-400 text-base">Got questions? We've got answers!</p>
        </div>

        {/* Mobile Layout */}
        {isMobile ? (
          <>
            {renderFaqBlock(visibleMobileFaqs)}
            {faqsData.length > 4 && (
              <div className="text-center mt-6">
                <button
                  onClick={() => setShowAllMobile((prev) => !prev)}
                  className="text-[16px] xs:text-[12px] border border-white text-white px-4 py-2 rounded-full"
                >
                  {showAllMobile ? 'Show Less' : 'Show More'}
                </button>
              </div>
            )}
          </>
        ) : (
          // Desktop two-column layout
          <div className="flex gap-8">
            <div className="w-1/2">{renderFaqBlock(leftFAQs)}</div>
            <div className="w-1/2">{renderFaqBlock(rightFAQs)}</div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Faqs;
